/**
 * @file    resolveRender.ts
 * @scope   SHARED
 * @summary Read-path counterpart of the drawer resolver: decides how a persisted widget renders
 *          (legacy, engine subtype, or unknown placeholder) from the registry.
 * @consumes readEnvelope
 * @produces RenderableDescriptor, RenderResolution, RenderResolveOptions, createRenderResolver
 * @sideEffects None.
 * @notes   Phase 6. Registry-agnostic (descriptors injected, DIP). Gated-off or unregistered
 *          `ChartTypeId` → `unknown` (Null Object), never a crash or a silent fallback to another
 *          subtype. Widgets without `ChartTypeId` resolve to the default subtype (today's behaviour).
 */
import { readEnvelope } from '../serialization/envelope';

export type RenderableDescriptor = {
  id: string;
  component: string;
  featureFlag?: string;
  renderBinding?: { component: string };
};

export type RenderResolution<TDescriptor> =
  | { status: 'legacy' }
  | { status: 'engine'; descriptor: TDescriptor; component: string }
  | { status: 'unknown'; chartTypeId: string };

export type RenderResolveOptions = {
  /** Result of the family check (`isDiyWidgetEngine`); routes into the engine at all. */
  isEngine: boolean;
  /** Engine flag resolver (localStorage → config → default). */
  isFlagEnabled: (flag?: string) => boolean;
};

/**
 * @param descriptors Registered subtypes.
 * @param defaultChartTypeId Subtype for configs saved before the envelope existed.
 * @returns A pure `(config, options) => RenderResolution` function.
 */
export const createRenderResolver =
  <TDescriptor extends RenderableDescriptor>(
    descriptors: TDescriptor[],
    defaultChartTypeId: string
  ) =>
  (config: unknown, { isEngine, isFlagEnabled }: RenderResolveOptions) => {
    if (!isEngine) return { status: 'legacy' } as RenderResolution<TDescriptor>;

    const { chartTypeId } = readEnvelope(
      config,
      defaultChartTypeId,
      descriptors.map((d) => d.id)
    );
    const descriptor = descriptors.find((d) => d.id === chartTypeId);

    if (!descriptor || !isFlagEnabled(descriptor.featureFlag)) {
      return { status: 'unknown', chartTypeId } as RenderResolution<TDescriptor>;
    }

    return {
      status: 'engine',
      descriptor,
      component: descriptor.renderBinding?.component ?? descriptor.component,
    } as RenderResolution<TDescriptor>;
  };
